import React, { useRef } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import SectionIntro from "./SectionIntro";

const TO_EMAIL = "laurierroseourika@gmail.com";

const Transfer = () => {
  const { t } = useTranslation();
  const form = useRef();

  const today = new Date().toISOString().split("T")[0];

  const steps = ["s1", "s2", "s3", "s4"];
  const included = ["i1", "i2", "i3", "i4", "i5", "i6"];

  // Build a pre-written email and open the guest's mail app to review & send.
  const sendRequest = (e) => {
    e.preventDefault();
    const fd = new FormData(form.current);
    const direction = fd.get("direction") || "toVilla";
    const lines = [
      `${t("transfer.fullName")}: ${fd.get("name") || ""}`,
      `${t("transfer.phone")}: ${fd.get("phone") || ""}`,
      `${t("transfer.flightNumber")}: ${fd.get("flight") || ""}`,
      `${t("transfer.arrivalDate")}: ${fd.get("arrival_date") || ""} ${
        fd.get("arrival_time") || ""
      }`.trim(),
      `${t("transfer.passengers")}: ${fd.get("passengers") || ""}`,
      `${t("transfer.direction")}: ${t(`transfer.${direction}`)}`,
      `${t("transfer.notes")}: ${fd.get("notes") || ""}`,
    ];
    const subject = `${t("transfer.eyebrow")} — ${fd.get("name") || ""}`.trim();
    const body = `${t("transfer.mailIntro")}\n\n${lines.join("\n")}`;
    // Open Gmail's compose window in a new tab (reliable even with no default mail app).
    const gmail = `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(
      TO_EMAIL
    )}&su=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.open(gmail, "_blank", "noopener,noreferrer");
  };

  const inputCls =
    "w-full px-4 py-3 border border-ink/15 bg-white focus:ring-2 focus:ring-brass focus:border-transparent transition-all duration-300";

  return (
    <section className="bg-cream pt-32 md:pt-40 pb-24">
      <div className="max-w-5xl mx-auto px-5 md:px-10">
        <SectionIntro
          eyebrow={t("transfer.eyebrow")}
          title={t("transfer.title")}
          lead={t("transfer.lead")}
          className="mb-16 md:mb-20"
        />

        {/* How it works */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8 mb-20 md:mb-28">
          {steps.map((s, i) => (
            <motion.div
              key={s}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.6, delay: i * 0.08 }}
            >
              <span className="font-serif text-4xl font-light text-brass/40">
                {String(i + 1).padStart(2, "0")}
              </span>
              <h3 className="font-serif text-xl text-ink mt-3 mb-2">
                {t(`transfer.steps.${s}.title`)}
              </h3>
              <p className="text-ink-soft font-light text-sm leading-relaxed">
                {t(`transfer.steps.${s}.desc`)}
              </p>
            </motion.div>
          ))}
        </div>

        {/* Included + Vehicles */}
        <div className="grid lg:grid-cols-2 gap-12 md:gap-16 mb-20 md:mb-24">
          <div>
            <h2 className="font-serif font-light text-2xl md:text-3xl text-ink">
              {t("transfer.includedTitle")}
            </h2>
            <div className="rule-gold my-5" />
            <ul className="space-y-3">
              {included.map((i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="text-brass mt-1">◆</span>
                  <span className="text-ink-soft font-light">
                    {t(`transfer.included.${i}`)}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h2 className="font-serif font-light text-2xl md:text-3xl text-ink">
              {t("transfer.vehiclesTitle")}
            </h2>
            <div className="rule-gold my-5" />
            <div className="space-y-3">
              {[t("transfer.sedan"), t("transfer.minivan")].map((v) => (
                <div
                  key={v}
                  className="flex items-center justify-between border border-ink/10 bg-white px-5 py-4"
                >
                  <span className="font-serif text-lg text-ink">{v}</span>
                  <span className="eyebrow text-brass text-[10px]">
                    {t("transfer.priceOnRequest")}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Request form → opens a pre-written email */}
        <div className="max-w-2xl mx-auto">
          <h2 className="font-serif font-light text-3xl md:text-4xl text-ink text-center">
            {t("transfer.formTitle")}
          </h2>
          <div className="rule-gold mx-auto my-6" />

          <form ref={form} onSubmit={sendRequest} className="space-y-5">
            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <label className="block text-ink-soft mb-2 text-sm">
                  {t("transfer.fullName")} *
                </label>
                <input type="text" name="name" required className={inputCls} />
              </div>
              <div>
                <label className="block text-ink-soft mb-2 text-sm">
                  {t("transfer.phone")}
                </label>
                <input type="tel" name="phone" className={inputCls} />
              </div>
              <div>
                <label className="block text-ink-soft mb-2 text-sm">
                  {t("transfer.flightNumber")}
                </label>
                <input type="text" name="flight" className={inputCls} />
              </div>
              <div>
                <label className="block text-ink-soft mb-2 text-sm">
                  {t("transfer.passengers")}
                </label>
                <select name="passengers" className={inputCls} defaultValue="2">
                  {[1, 2, 3, 4, 5, 6, 7].map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-ink-soft mb-2 text-sm">
                  {t("transfer.arrivalDate")} *
                </label>
                <input
                  type="date"
                  name="arrival_date"
                  required
                  min={today}
                  className={inputCls}
                />
              </div>
              <div>
                <label className="block text-ink-soft mb-2 text-sm">
                  {t("transfer.arrivalTime")}
                </label>
                <input type="time" name="arrival_time" className={inputCls} />
              </div>
            </div>
            <div>
              <label className="block text-ink-soft mb-2 text-sm">
                {t("transfer.direction")}
              </label>
              <select name="direction" className={inputCls} defaultValue="toVilla">
                <option value="toVilla">{t("transfer.toVilla")}</option>
                <option value="toAirport">{t("transfer.toAirport")}</option>
                <option value="both">{t("transfer.both")}</option>
              </select>
            </div>
            <div>
              <label className="block text-ink-soft mb-2 text-sm">
                {t("transfer.notes")}
              </label>
              <textarea
                name="notes"
                rows="3"
                placeholder={t("transfer.notesPlaceholder")}
                className={inputCls}
              />
            </div>
            <button
              type="submit"
              className="w-full bg-ink hover:bg-brass text-cream py-4 text-[12px] uppercase tracking-[0.22em] transition-colors duration-300"
            >
              {t("transfer.submit")}
            </button>
            <p className="text-center text-ink-muted text-sm">
              {t("transfer.mailHint")}{" "}
              <a href={`mailto:${TO_EMAIL}`} className="text-brass hover:underline">
                {TO_EMAIL}
              </a>
            </p>
          </form>
        </div>
      </div>
    </section>
  );
};

export default Transfer;
